import * as io from "io-ts";
import { useCallback, useState } from "react";
import {
  aggregationIntervalFromPeriod,
  AggregationPeriod,
  mapIntervalFromEntities,
  type EntityWithInterval,
} from "../../../domain/aggregation/period";
import useGroups from "../../../api/useGroups";
import createGlobalState from "@ui/lib/createGlobalState";

const useGlobalSharedAggregationIntervalState = createGlobalState({
  storage: "local",
  key: "use-sync-aggregation-interval-state",
  type: io.strict({
    period: io.union([AggregationPeriod, io.null]),
  }),
  scoped: true,
});

export const useAggregationIntervalFromPeriodState = ({
  aggregationPeriod,
  projectId,
  projectTimezone,
  shared,
  from,
}: {
  aggregationPeriod: AggregationPeriod;
  projectId: string;
  projectTimezone: string;
  shared: boolean;
  from: Date;
}) => {
  const [localAggregationInterval, setLocalAggregationInterval] = useLocalAggregationIntervalState({
    aggregationPeriod,
    projectId,
    projectTimezone,
    limit: { from, to: new Date() },
  });
  const [sharedAggregationInterval, setSharedAggregationInterval] =
    useSharedAggregationIntervalState({
      aggregationPeriod,
      projectId,
      projectTimezone,
      limit: { from, to: new Date() },
    });

  const interval = shared ? sharedAggregationInterval : localAggregationInterval;
  const setInterval = shared ? setSharedAggregationInterval : setLocalAggregationInterval;

  return [interval, setInterval] as const;
};

const useLocalAggregationIntervalState = ({
  aggregationPeriod,
  projectId,
  projectTimezone,
  limit,
}: {
  aggregationPeriod: AggregationPeriod;
  projectId: string;
  projectTimezone: string;
  limit: { from: Date; to: Date };
}) => {
  const groups = useGroups(projectId);
  const defaultAggregationInterval = useDefaultAggregationInterval({
    aggregationPeriod,
    projectTimezone,
    groups,
    limit,
  });
  return useState(defaultAggregationInterval);
};

const useSharedAggregationIntervalState = ({
  aggregationPeriod,
  projectId,
  projectTimezone,
  limit,
}: {
  aggregationPeriod: AggregationPeriod;
  projectId: string;
  projectTimezone: string;
  limit: { from: Date; to: Date };
}) => {
  const groups = useGroups(projectId);
  const [syncAggregationInterval, setSyncAggregationInterval] =
    useGlobalSharedAggregationIntervalState({
      scope: projectId,
      defaultValue: {
        period: aggregationPeriod,
      },
    });

  const effectivePeriod = syncAggregationInterval.period ?? aggregationPeriod;
  const aggregationInterval = computeAggregationIntervalFromPeriod({
    aggregationPeriod: effectivePeriod,
    projectTimezone,
    groups,
    limit,
  });

  const setAggregationInterval = useCallback(
    (interval: { from: Date; to: Date; period: AggregationPeriod | null }) => {
      setSyncAggregationInterval({
        period: interval.period,
      });
    },
    [setSyncAggregationInterval],
  );

  return [aggregationInterval, setAggregationInterval] as const;
};

const useDefaultAggregationInterval = ({
  aggregationPeriod,
  projectTimezone,
  groups,
  limit,
}: {
  aggregationPeriod: AggregationPeriod;
  projectTimezone: string;
  groups: EntityWithInterval[];
  limit: { from: Date; to: Date };
}) =>
  computeAggregationIntervalFromPeriod({
    aggregationPeriod,
    projectTimezone,
    groups,
    limit,
  });

const computeAggregationIntervalFromPeriod = ({
  aggregationPeriod,
  projectTimezone,
  groups,
  limit,
  from,
}: {
  aggregationPeriod: AggregationPeriod;
  projectTimezone: string;
  groups: EntityWithInterval[];
  limit: { from: Date; to: Date };
  from?: Date;
}) => {
  if (aggregationPeriod === "CYCLE" || aggregationPeriod === "CYCLE_2") {
    return aggregationIntervalFromPeriod({
      period: aggregationPeriod,
      timezone: projectTimezone,
      intervals: mapIntervalFromEntities(groups),
      from,
    });
  }
  if (aggregationPeriod === "ALL") {
    return aggregationIntervalFromPeriod({
      period: aggregationPeriod,
      timezone: projectTimezone,
      limit,
    });
  }
  return aggregationIntervalFromPeriod({
    period: aggregationPeriod,
    timezone: projectTimezone,
    from,
  });
};
